import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, IconButton } from "@mui/material";
import {
  Image as ImageIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  MODAL_IMAGE_EDIT_IMAGE_ALT,
  MODAL_IMAGE_EDIT_IMAGE_NOT_FOUND,
  MODAL_IMAGE_EDIT_START_CREATING,
  MODAL_IMAGE_EDIT_EDITING_LABEL,
  MODAL_IMAGE_EDIT_LOADING_LABEL,
} from "../../../../i18n/keysTranslations";
import ImageActions from "./ImageActions";

const ImageCanvas = ({
  imageUrl,
  initialImageUrl,
  isEditing,
  isSidebarOpen,
  onToggleSidebar,
  onClose,
  onCallbackSave,
  showSaveAlert = true,
  showCloseButton = true,
  showSaveActions = true,
}) => {
  const theme = useTheme();
  const { t } = useTranslationApp();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [previousImageUrl, setPreviousImageUrl] = useState(imageUrl);
  const [wasEditing, setWasEditing] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setWasEditing(true);
      return;
    }
    if (!isImageLoading) {
      setWasEditing(false);
    }
  }, [isEditing, isImageLoading]);

  useEffect(() => {
    if (imageUrl && imageUrl !== previousImageUrl) {
      setIsImageLoading(true);
      setPreviousImageUrl(imageUrl);
    }
  }, [imageUrl, previousImageUrl]);

  const onImageLoad = () => {
    setIsImageLoading(false);
    setWasEditing(false);
  };

  const onImageError = () => {
    setIsImageLoading(false);
    setWasEditing(false);
  };

  const showOverlay = isEditing || isImageLoading || (wasEditing && !isEditing);
  const overlayMessage = isEditing
    ? t(MODAL_IMAGE_EDIT_EDITING_LABEL)
    : t(MODAL_IMAGE_EDIT_LOADING_LABEL);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
        maxHeight: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 4 },
          pt: { xs: showCloseButton ? 6 : 2, sm: showCloseButton ? 6 : 2 },
          pb: { xs: 1, sm: 2 },
          overflow: "hidden",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.03)"
              : "rgba(0,0,0,0.02)"
          } 0%, ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.04)"
          } 100%)`,
        }}
      >
        {!isSidebarOpen ? (
          <IconButton
            onClick={onToggleSidebar}
            sx={{
              position: "absolute",
              top: 15,
              left: 10,
              zIndex: 10,
            }}
          >
            <MenuIcon />
          </IconButton>
        ) : null}

        {showCloseButton ? (
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 15,
              right: 6,
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}

        {imageUrl ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "100%",
              maxHeight: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: "100%",
                maxHeight: "100%",
                width: "100%",
                height: "100%",
                padding: 2,
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt={t(MODAL_IMAGE_EDIT_IMAGE_ALT)}
                onLoad={onImageLoad}
                onError={onImageError}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: 2,
                  boxShadow: 3,
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  opacity: showOverlay ? 0.5 : 1,
                  transition: "opacity 0.3s ease",
                  display: "block",
                }}
              />
              {showOverlay ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    right: 16,
                    bottom: 16,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    zIndex: 1,
                  }}
                >
                  <CircularProgress sx={{ color: "#000000" }} />
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#000000",
                      fontWeight: 500,
                    }}
                  >
                    {overlayMessage}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 96,
                height: 96,
                mx: "auto",
                mb: 2,
                borderRadius: "50%",
                backgroundColor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ImageIcon sx={{ fontSize: 48, color: "text.secondary" }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
              {t(MODAL_IMAGE_EDIT_IMAGE_NOT_FOUND)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 400 }}
            >
              {t(MODAL_IMAGE_EDIT_START_CREATING)}
            </Typography>
          </Box>
        )}
      </Box>

      {showSaveActions ? (
        <ImageActions
          imageUrl={imageUrl}
          initialImageUrl={initialImageUrl}
          onCallbackSave={onCallbackSave}
          onClose={onClose}
          showSaveAlert={showSaveAlert}
        />
      ) : null}
    </Box>
  );
};

export default ImageCanvas;
